import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Descriptions,
  Tag,
  Alert,
  Spin,
  Empty,
  message,
  Table,
  AutoComplete,
} from 'antd';
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { formatDateTime } from '../../utils/helpers';
import api from '../../utils/api';
import EmployeeAvatar from '../../components/EmployeeAvatar';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const ResultCard = styled(Card)`
  border-radius: 12px !important;
  margin-top: 24px;
  background: linear-gradient(135deg, #F7FAFC 0%, #FFFFFF 100%);
`;

const LocationTag = styled(Tag)`
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 8px;
`;

const TableWrapper = styled.div`
  .ant-table-body {
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const SearchWrapper = styled.div`
  .ant-select-auto-complete {
    .ant-select-selector {
      border-radius: 12px !important;
      border: 2px solid #e8e8e8 !important;
      padding: 0 16px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
      height: 46px !important;
      background: #ffffff;
      display: flex !important;
      align-items: center !important;

      &:hover {
        border-color: #B4A0F7 !important;
        box-shadow: 0 4px 12px rgba(180, 160, 247, 0.15) !important;
      }

      .ant-select-selection-search {
        display: flex !important;
        align-items: center !important;
      }

      .ant-select-selection-search-input {
        font-size: 15px;
        height: 46px !important;
        line-height: 46px !important;

        &::placeholder {
          color: #a0a0a0;
          font-weight: 400;
        }
      }

      .ant-select-selection-placeholder {
        color: #a0a0a0;
        font-size: 15px;
        line-height: 46px !important;
      }
    }

    &.ant-select-focused .ant-select-selector {
      border-color: #B4A0F7 !important;
      box-shadow: 0 4px 16px rgba(180, 160, 247, 0.25) !important;
    }
  }

  /* Dropdown options styling */
  .ant-select-dropdown {
    .rc-virtual-list-holder-inner {
      .ant-select-item {
        min-height: 72px !important;
        padding: 8px 12px !important;

        &:hover {
          background: #F7F5FF !important;
        }
      }
    }
  }

  .search-button {
    border-radius: 12px;
    font-weight: 600;
    height: 46px !important;
    padding: 0 24px;
    background: linear-gradient(135deg, #B4A0F7 0%, #9B7FE8 100%);
    border: none;
    box-shadow: 0 2px 8px rgba(180, 160, 247, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: linear-gradient(135deg, #9B7FE8 0%, #8B6AD8 100%) !important;
      box-shadow: 0 4px 12px rgba(180, 160, 247, 0.4) !important;
    }

    &:active {
      transform: translateY(0px);
    }
  }
`;

const FindPerson = () => {
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'list'

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Autocomplete
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);

  // Debounce timer refs
  const debounceTimer = useRef(null);
  const suggestionDebounceTimer = useRef(null);
  const isSearchingRef = useRef(false);
  const lastSearchValue = useRef('');

  const handleSearch = useCallback(async (value) => {
    if (!value || !value.trim()) {
      message.warning('Please enter an employee ID, name, or email');
      return;
    }

    setLoading(true);
    setError(null);
    setEmployee(null);
    setSearchResults([]);

    try {
      // Search in employee list
      const listResult = await api.getEmployeeList({
        search: value.trim(),
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      if (listResult.data && listResult.data.length > 0) {
        setTotal(listResult.total);
        if (listResult.data.length === 1) {
          // Single result - show detailed view
          setEmployee(listResult.data[0]);
          setViewMode('single');
        } else {
          // Multiple results - show list view
          setSearchResults(listResult.data);
          setViewMode('list');
        }
      } else {
        setError(`No employee found matching "${value}"`);
      }
    } catch (err) {
      setError(err.message || 'Failed to search employee');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  const handleSearchSuggestions = (searchValue) => {
    // Clear previous timer
    if (suggestionDebounceTimer.current) {
      clearTimeout(suggestionDebounceTimer.current);
    }

    if (!searchValue || searchValue.length < 2) {
      setEmployeeSuggestions([]);
      lastSearchValue.current = '';
      return;
    }

    // Skip if already searching or same value
    if (isSearchingRef.current || lastSearchValue.current === searchValue) {
      return;
    }

    // Debounce the search to reduce API calls
    suggestionDebounceTimer.current = setTimeout(async () => {
      // Double check before making API call
      if (isSearchingRef.current || lastSearchValue.current === searchValue) {
        return;
      }

      try {
        isSearchingRef.current = true;
        lastSearchValue.current = searchValue;
        setSearchingEmployees(true);

        const result = await api.getEmployeeList({
          search: searchValue,
          limit: 10, // Get more results for better filtering
          offset: 0,
        });

        // Filter results to only include employees that actually match the search term
        const searchLower = searchValue.toLowerCase();
        const filteredData = result.data.filter((emp) => {
          const empIdMatch = emp.emp_id?.toLowerCase().includes(searchLower);
          const nameMatch = emp.full_name?.toLowerCase().includes(searchLower);
          const emailMatch = emp.email?.toLowerCase().includes(searchLower);
          return empIdMatch || nameMatch || emailMatch;
        });

        // Limit to top 5 matches
        const topMatches = filteredData.slice(0, 5);

        const suggestions = topMatches.map((emp) => ({
          value: emp.emp_id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px' }}>
              <Space size="middle">
                <EmployeeAvatar
                  empId={emp.emp_id}
                  size={56}
                  photoUpdatedAt={emp.photo_updated_at}
                  style={{
                    background: '#B4A0F7',
                    flexShrink: 0,
                    border: '2px solid #E8E4F7'
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}>
                    <Text strong>{emp.emp_id}</Text> - {emp.full_name}
                  </div>
                  {emp.email && <Text type="secondary" style={{ fontSize: 13 }}>{emp.email}</Text>}
                </div>
              </Space>
              {emp.department && <Tag color="blue" style={{ fontSize: '12px' }}>{emp.department}</Tag>}
            </div>
          ),
          empData: emp,
        }));

        setEmployeeSuggestions(suggestions);
      } catch (error) {
        console.error('Error searching employees:', error);
        setEmployeeSuggestions([]);
      } finally {
        setSearchingEmployees(false);
        isSearchingRef.current = false;
      }
    }, 400); // 400ms delay to reduce API calls while typing
  };

  const handleSelectSuggestion = (value, option) => {
    setSearchText(value);
    if (option.empData) {
      // Show directly in detailed view
      setEmployee(option.empData);
      setViewMode('single');
      setSearchResults([]);
    }
  };

  const loadData = useCallback(() => {
    if (searchText && searchText.trim()) {
      handleSearch(searchText);
    }
  }, [searchText, handleSearch]);

  useEffect(() => {
    if (searchText && viewMode === 'list') {
      loadData();
    }
  }, [page, pageSize, loadData, searchText, viewMode]);

  const handleViewDetails = (record) => {
    setEmployee(record);
    setViewMode('single');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEmployee(null);
  };

  const handleReset = () => {
    setSearchText('');
    setEmployee(null);
    setSearchResults([]);
    setError(null);
    setPage(1);
    setViewMode('single');
  };

  const renderEmployeeInfo = () => {
    if (!employee) return null;

    const isActive = employee.active === 'Y' || employee.active === true;
    const hasRecentActivity = employee.last_seen &&
      new Date() - new Date(employee.last_seen) < 24 * 60 * 60 * 1000; // Within 24 hours

    return (
      <ResultCard>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <EmployeeAvatar
              empId={employee.emp_id}
              size={80}
              enablePreview={true}
              photoUpdatedAt={employee.photo_updated_at}
              style={{
                background: 'linear-gradient(135deg, #B4A0F7 0%, #B4A0F7 100%)',
                boxShadow: '0 4px 12px rgba(180, 160, 247, 0.3)',
              }}
            />
            <div style={{ flex: 1 }}>
              <Title level={3} style={{ margin: 0 }}>
                {employee.full_name || employee.emp_id}
              </Title>
              <Text type="secondary" style={{ fontSize: 16 }}>
                ID: {employee.emp_id}
              </Text>
              <div style={{ marginTop: 8 }}>
                <Tag color={isActive ? 'green' : 'default'}>
                  {isActive ? 'Active' : 'Inactive'}
                </Tag>
                {hasRecentActivity && (
                  <Tag color="blue" icon={<CheckCircleOutlined />}>
                    Recently Active
                  </Tag>
                )}
              </div>
            </div>
          </div>

          {/* Current Location - Prioritize crossing-based location */}
          {(employee.last_crossing_zone || employee.last_seen_camera_zone || employee.last_seen_camera) ? (
            <Alert
              type="success"
              showIcon
              icon={<EnvironmentOutlined />}
              message={
                <div>
                  <Text strong style={{ fontSize: 16 }}>Current Location</Text>
                  <div style={{ marginTop: 8 }}>
                    <LocationTag color="green">
                      {employee.last_crossing_zone || employee.last_seen_camera_zone || employee.last_seen_camera}
                    </LocationTag>
                  </div>
                </div>
              }
              description={
                <div style={{ marginTop: 8 }}>
                  <Space direction="vertical" size={4}>
                    <Text>
                      <ClockCircleOutlined /> Last seen: {formatDateTime(employee.last_crossing_time || employee.last_seen)}
                    </Text>
                    {(employee.last_crossing_camera_id || employee.last_seen_camera_id) && (
                      <Text type="secondary">
                        Camera ID: {employee.last_crossing_camera_id || employee.last_seen_camera_id}
                      </Text>
                    )}
                  </Space>
                </div>
              }
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              icon={<CloseCircleOutlined />}
              message="Location Unknown"
              description="This person has not been detected recently"
            />
          )}

          {/* Employee Details */}
          <Card title="Employee Information" size="small">
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="Full Name" span={2}>
                {employee.full_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>
                {employee.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {employee.department ? (
                  <Tag color="blue">{employee.department}</Tag>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Team">
                {employee.team || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Division">
                {employee.division || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {employee.phone || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Activity Timeline */}
          <Card title="Last Activity" size="small">
            <div style={{ padding: '16px 0' }}>
              {(employee.last_crossing_time || employee.last_seen) && (
                <Space style={{ marginBottom: 12, width: '100%' }}>
                  <ClockCircleOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                  <div>
                    <Text strong>Last Seen</Text>
                    <br />
                    <Text type="secondary">
                      {formatDateTime(employee.last_crossing_time || employee.last_seen)}
                    </Text>
                  </div>
                </Space>
              )}
              {(employee.last_crossing_zone || employee.last_seen_camera_zone || employee.last_seen_camera) && (
                <Space style={{ marginBottom: 12, width: '100%' }}>
                  <EnvironmentOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                  <div>
                    <Text strong>Location</Text>
                    <br />
                    <Text type="secondary">
                      {employee.last_crossing_zone || employee.last_seen_camera_zone || employee.last_seen_camera}
                    </Text>
                  </div>
                </Space>
              )}
              {employee.first_seen && (
                <Space style={{ width: '100%' }}>
                  <ClockCircleOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
                  <div>
                    <Text strong>First Seen</Text>
                    <br />
                    <Text type="secondary">
                      {formatDateTime(employee.first_seen)}
                    </Text>
                  </div>
                </Space>
              )}
            </div>
          </Card>
        </Space>
      </ResultCard>
    );
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'emp_id',
      key: 'emp_id',
      render: (text, record) => (
        <Space>
          <EmployeeAvatar
            empId={record.emp_id}
            size={40}
            enablePreview={true}
            photoUpdatedAt={record.photo_updated_at}
            style={{ background: 'linear-gradient(135deg, #B4A0F7 0%, #B4A0F7 100%)' }}
          />
          <div>
            <div><Text style={{ fontWeight: 500 }}>{record.emp_id}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.full_name}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text) => text ? (
        <Tag
          bordered
          color="blue"
          style={{
            borderRadius: '4px',
            fontSize: '12px',
            padding: '2px 8px',
            fontWeight: 500
          }}
        >
          {text}
        </Tag>
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Last Location',
      dataIndex: 'last_crossing_zone',
      key: 'location',
      render: (text, record) => {
        const location = text || record.last_seen_camera_zone || record.last_seen_camera;
        const timestamp = record.last_crossing_time || record.last_seen;
        return location ? (
          <Space direction="vertical" size={0}>
            <Text><EnvironmentOutlined /> {location}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatDateTime(timestamp)}
            </Text>
          </Space>
        ) : <Text type="secondary">Unknown</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (text) => (
        <Space size={6}>
          <span style={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: text === 'Y' ? '#10B981' : '#9CA3AF'
          }} />
          <span style={{ color: '#374151', fontSize: '13px' }}>
            {text === 'Y' ? 'Active' : 'Inactive'}
          </span>
        </Space>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewDetails(record)}
          style={{ color: '#6366F1' }}
        />
      ),
    },
  ];

  return (
    <div>
      {(employee || searchResults.length > 0) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12 }}>
          <Button onClick={handleReset}>
            New Search
          </Button>
        </div>
      )}

      <StyledCard>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* Search Bar */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Search for an employee by their ID, name, or email to see their current location
            </Text>
            <SearchWrapper>
              <div style={{ display: 'flex', gap: '12px', width: '100%', alignItems: 'center' }}>
                <AutoComplete
                  value={searchText}
                  options={employeeSuggestions}
                  onSearch={handleSearchSuggestions}
                  onSelect={handleSelectSuggestion}
                  onChange={(value) => {
                    setSearchText(value);
                    // Don't trigger debouncedSearch here - only use autocomplete suggestions
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (debounceTimer.current) {
                        clearTimeout(debounceTimer.current);
                      }
                      if (suggestionDebounceTimer.current) {
                        clearTimeout(suggestionDebounceTimer.current);
                      }
                      handleSearch(searchText);
                    }
                  }}
                  placeholder="Type employee ID, name, or email to find location..."
                  style={{ flex: 1 }}
                  notFoundContent={searchingEmployees ? <Spin size="small" /> : null}
                  popupMatchSelectWidth={600}
                  dropdownStyle={{ minWidth: '600px' }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => handleSearch(searchText)}
                  className="search-button"
                  loading={loading}
                >
                  Search
                </Button>
              </div>
            </SearchWrapper>
          </div>

          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" tip="Searching..." />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <Empty
              description={
                <Space direction="vertical">
                  <Text type="secondary">{error}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Try searching with employee ID, full name, or email
                  </Text>
                </Space>
              }
            />
          )}

          {/* Single Employee View */}
          {employee && !loading && viewMode === 'single' && (
            <>
              {searchResults.length > 0 && (
                <Button type="link" onClick={handleBackToList}>
                  ← Back to search results
                </Button>
              )}
              {renderEmployeeInfo()}
            </>
          )}

          {/* List View with Pagination */}
          {searchResults.length > 0 && !loading && viewMode === 'list' && (
            <div>
              <div style={{ marginBottom: 12 }}>
                <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                  Found {total} employee{total !== 1 ? 's' : ''}
                </Tag>
              </div>
              <TableWrapper>
                <Table
                  size="small"
                  columns={columns}
                  dataSource={searchResults}
                  rowKey="id"
                  scroll={{ x: 1200, y: 'calc(100vh - 400px)' }}
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} employees`,
                    onChange: (page, pageSize) => {
                      setPage(page);
                      setPageSize(pageSize);
                    },
                    responsive: true,
                  }}
                />
              </TableWrapper>
            </div>
          )}
        </Space>
      </StyledCard>
    </div>
  );
};

export default FindPerson;
